import React, { useMemo } from 'react';

interface WindBarbProps {
  speed: number;
  gust: number;
  direction: number;
  temp: number;
  rh: number;
  size?: number;
  renderPart?: 'all' | 'shaft-barbs' | 'head';
  /**
   * Uniform render multiplier. >1 draws the same barb at higher resolution AND
   * larger pixel size (crisp when displayed bigger). Default 1 leaves the legend
   * untouched; the map symbol layer passes 2×devicePixelRatio for crisp, larger
   * station icons.
   */
  scale?: number;
}

/**
 * Color for the station marker head circle, by relative humidity (%).
 * Blue (humid) → Green → Yellow → Orange (very dry).
 */
export function getMarkerRhColor(rh: number): string {
  if (rh >= 70) return '#0077ff';  // Blue (humid)
  if (rh >= 51) return '#00aa55';  // Green (moderate)
  if (rh >= 31) return '#e1ed02';  // Yellow (dry)
  return '#FFA500';                // Orange (very dry)
}

/**
 * Text color for the temperature label on the marker head, chosen for contrast
 * against the head color. Black on the light yellow band; white otherwise.
 */
export function getMarkerTextColor(rh: number): string {
  // The dry side — yellow (#e1ed02) and orange (#FFA500) heads — is too light
  // for white text. Blue/green heads keep white.
  if (rh < 51) return '#000000';
  return '#ffffff';
}

/**
 * Draw a wind barb onto a fresh canvas and return the canvas (NOT a dataURL).
 *
 * This is the single source of truth for the barb visual. The `WindBarb` React
 * component renders this canvas to a PNG dataURL for DOM use; the
 * WeatherLayerProvider registers the same canvas as a Mapbox `addImage` icon for
 * a GPU symbol layer. Drawing behavior (rotation baked in, head color via
 * `getMarkerRhColor`, upright temp label, barb generation) is identical to the
 * previous inline `useMemo`.
 */
export function renderWindBarbCanvas({
  speed,
  gust,
  direction,
  temp,
  rh,
  size = 50,
  renderPart = 'all',
  scale = 1,
}: WindBarbProps): HTMLCanvasElement {
  const padding = 20;
  const baseSize = size + padding * 2;

  const canvas = document.createElement('canvas');
  canvas.width = baseSize * scale;
  canvas.height = baseSize * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // Draw at `scale` resolution so the barb is both crisp and `scale`× larger,
  // then center within the (unscaled) logical canvas.
  ctx.scale(scale, scale);
  ctx.translate(baseSize / 2, baseSize / 2);
  ctx.rotate((direction * Math.PI) / 180);

  const color = getMarkerRhColor(rh);
  const fullBarbLength = 12;
  const halfBarbLength = 6;
  const barbSpacing = 8;
  const shaftLength = 60;

  const generateBarbs = (value: number) => {
    const rounded = Math.round(value / 5) * 5;
    const full = Math.floor(rounded / 10);
    const half = rounded % 10 === 5 ? 1 : 0;
    const barbs = [];

    for (let i = 0; i < full; i++) {
      barbs.push({ length: fullBarbLength });
    }
    if (half) {
      barbs.push({ length: halfBarbLength });
    }
    return barbs;
  };

  const drawBarbs = (barbs: { length: number }[], color: string) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    let y = -shaftLength;

    for (let i = barbs.length - 1; i >= 0; i--) {
      const { length } = barbs[i];
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(length, y - length);
      ctx.stroke();
      y += barbSpacing;
    }
  };

  // Gust barbs with halo — commented out per #323 (non-standard symbology confusing users)
  // const drawBarbsWithHalo = (barbs: { length: number }[], haloColor: string, mainColor: string) => {
  //   let y = -shaftLength;
  //
  //   for (let i = barbs.length - 1; i >= 0; i--) {
  //     const { length } = barbs[i];
  //
  //     // Draw black halo first (wider)
  //     ctx.strokeStyle = haloColor;
  //     ctx.lineWidth = 4;
  //     ctx.beginPath();
  //     ctx.moveTo(0, y);
  //     ctx.lineTo(length, y - length);
  //     ctx.stroke();
  //
  //     // Draw yellow barb on top (thinner)
  //     ctx.strokeStyle = mainColor;
  //     ctx.lineWidth = 2;
  //     ctx.beginPath();
  //     ctx.moveTo(0, y);
  //     ctx.lineTo(length, y - length);
  //     ctx.stroke();
  //
  //     y += barbSpacing;
  //   }
  // };

  // Render based on renderPart parameter
  if (renderPart === 'all' || renderPart === 'shaft-barbs') {
    // Draw barbs and shaft
    // drawBarbsWithHalo(generateBarbs(gust), 'black', '#ffff00');
    drawBarbs(generateBarbs(speed), color);

    // Shaft (drawn on top of barbs)
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, -shaftLength);
    ctx.stroke();
  }

  if (renderPart === 'all' || renderPart === 'head') {
    // Head circle
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(0, 0, 12, 0, 2 * Math.PI);
    ctx.fill();

    // Upright temperature label (on top of head)
    ctx.save();
    ctx.rotate((-direction * Math.PI) / 180);
    ctx.fillStyle = getMarkerTextColor(rh);
    ctx.font = 'bold 15px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${Math.round(temp)}`, 0, 5);
    ctx.restore();
  }

  return canvas;
}

export const WindBarb: React.FC<WindBarbProps> = ({
  speed,
  gust,
  direction,
  temp,
  rh,
  size = 50,
  renderPart = 'all',
}) => {
  const dataUrl = useMemo(
    () =>
      renderWindBarbCanvas({ speed, gust, direction, temp, rh, size, renderPart }).toDataURL(
        'image/png',
      ),
    [speed, gust, direction, temp, rh, size, renderPart],
  );

  const padding = 20;
  const displaySize = size + padding * 2;

  return <img src={dataUrl} width={displaySize} height={displaySize} alt="Wind barb" />;
};
